/**
 * Como um elemento se chama numa lista.
 *
 * Puro pelo mesmo motivo de `passos.ts`: **este arquivo não importa nada**, então
 * `cli/tests/js/passos-de-apresentacao.test.ts` o executa nesta estação sem os ~16 min de
 * build da Regra 8.7.3.
 *
 * ⚠️ **Não existe "nome do elemento" no BlockSuite.** Uma forma não tem título; uma imagem tem
 * `caption`, que costuma estar vazia; uma nota tem parágrafos. A lista de passos precisa de
 * ALGUMA etiqueta, e a alternativa a esta função seria mostrar o id — que não diz nada a
 * ninguém. O que se faz aqui é escolher, em ordem, o primeiro campo que existe e é humano.
 */

/** O que a lista consegue ler de um elemento sem conhecer o tipo dele. */
export interface ElementoRotulavel {
  type?: string;
  flavour?: string;
  /** Formas e textos: o texto pode ser `Y.Text`, e `toString()` já resolve. */
  text?: { toString(): string } | string | null;
  props?: {
    title?: { toString(): string } | string | null;
    caption?: string | null;
  } | null;
  shapeType?: string;
}

const NOMES: Record<string, string> = {
  shape: 'Forma',
  text: 'Texto',
  connector: 'Conector',
  brush: 'Traço',
  highlighter: 'Marca-texto',
  group: 'Grupo',
  mindmap: 'Mapa mental',
  'affine:note': 'Nota',
  'affine:image': 'Imagem',
  'affine:frame': 'Quadro',
  'affine:embed-linked-doc': 'Documento',
  'affine:embed-synced-doc': 'Documento',
  'affine:attachment': 'Anexo',
  'affine:bookmark': 'Link',
  'affine:edgeless-text': 'Texto',
  'tuninho:chart': 'Gráfico',
};

/** Corta sem cortar palavra no meio quando dá, e sinaliza o corte. */
function encurtar(s: string, limite = 28): string {
  const limpo = s.replace(/\s+/g, ' ').trim();
  if (limpo.length <= limite) return limpo;
  return limpo.slice(0, limite - 1).trimEnd() + '…';
}

function texto(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof (v as { toString?: unknown }).toString === 'function') {
    const s = String(v);
    // `Y.Text` sem conteúdo vira ''; objeto sem `toString` próprio vira '[object Object]',
    // que seria pior que não mostrar nada.
    return s === '[object Object]' ? '' : s;
  }
  return '';
}

/**
 * A etiqueta de um elemento. Nunca devolve string vazia — a lista precisa de algo clicável.
 *
 * A ordem é: **conteúdo humano** (texto/título/legenda) → **tipo traduzido** → `Elemento`.
 */
export function rotuloDoElemento(elemento: unknown): string {
  if (!elemento || typeof elemento !== 'object') return 'Elemento';
  const e = elemento as ElementoRotulavel;

  const conteudo =
    texto(e.text) || texto(e.props?.title) || texto(e.props?.caption);
  if (conteudo.trim()) return encurtar(conteudo);

  const chave = e.flavour ?? e.type ?? '';
  const nome = NOMES[chave];
  if (nome) {
    // Uma forma diz QUAL forma quando sabe — "Retângulo" ajuda mais que "Forma".
    if (chave === 'shape' && e.shapeType) {
      const forma = FORMAS[e.shapeType];
      if (forma) return forma;
    }
    return nome;
  }

  // Flavour desconhecido (bloco de terceiro, ou nosso, ainda não catalogado): mostrar a parte
  // depois do `:` é melhor que "Elemento" — diz ao menos de que família é.
  const sufixo = chave.includes(':') ? chave.split(':').pop() : chave;
  return sufixo ? encurtar(sufixo) : 'Elemento';
}

const FORMAS: Record<string, string> = {
  rect: 'Retângulo',
  ellipse: 'Elipse',
  triangle: 'Triângulo',
  diamond: 'Losango',
  roundedRect: 'Retângulo',
};
